/**
 * era/controller.ts — pure math for the era crossfade.
 *
 * Source: 04-RESEARCH.md Pattern 2 + Pitfall #4.
 *
 * Why pure functions (no THREE.js, no React):
 *   - Unit tests run in node env without DOM/WebGL boot cost (ms, not seconds)
 *   - EraGroup.tsx is the ONLY consumer that imports THREE; this file stays
 *     framework-agnostic so future refactors (shader-based crossfade, v2) can
 *     swap the driver without rewriting the math
 *
 * Damp formula (same as THREE.MathUtils.damp):
 *   next = lerp(current, target, 1 - exp(-lambda * dt))
 *   lambda = ln(2) / halfLifeSeconds
 *
 * At 200ms half-life, the tween reaches:
 *   - ~50%  at 200ms (1 half-life)
 *   - ~75%  at 400ms (2 half-lives)
 *   - ~96%  at 800ms (~4 half-lives)
 *   - ~99%  at ~1000ms (~5 half-lives) → effectively settled
 *
 * The Aesthetic Brief Rule 10 "400ms crossfade" refers to the visually-done
 * window; the damp keeps damping past that but the eye stops noticing.
 *
 * Pitfall #4 (Re-targetability):
 *   damp(current, newTarget, lambda, dt) always moves `current` toward the
 *   LATEST `target`, regardless of how many times target has changed between
 *   frames. No state machine, no startTime — rapid era toggles just work.
 */

export type Era = '1925' | '2026' | 'shared';

/**
 * Compute the opacity target for an era group given the current scene era.
 *
 * - 'shared' groups never fade (always 1)
 * - Matching era → visible (1)
 * - Mismatched era → hidden (0)
 */
export function computeTargetOpacity(
  groupEra: Era,
  currentEra: '1925' | '2026',
): number {
  if (groupEra === 'shared') return 1;
  return groupEra === currentEra ? 1 : 0;
}

/**
 * Damp a scalar toward a target over one frame.
 *
 * Frame-rate independent: same half-life produces same perceptual speed at
 * 30fps, 60fps, 120fps. dt <= 0 is a defensive no-op (guards against paused
 * frames or clock drift).
 */
export function dampToTarget(
  current: number,
  target: number,
  halfLifeMs: number,
  dt: number,
): number {
  if (dt <= 0) return current;
  const lambda = Math.LN2 / (halfLifeMs / 1000);
  const blend = 1 - Math.exp(-lambda * dt);
  return current + (target - current) * blend;
}

/**
 * Is the tween effectively settled?
 *
 * Compares per-frame delta against epsilon. Used by EraGroup.tsx to flip the
 * eraTransitioning flag off once the max-delta across all materials has fallen
 * below threshold — signals UI that the blend is complete.
 */
export function isSettled(prev: number, current: number, epsilon = 0.001): boolean {
  return Math.abs(current - prev) < epsilon;
}

/**
 * Half-life for the era crossfade, in milliseconds.
 *
 * 200ms → ~400ms visually-done (2 half-lives, ~75% settled) → ~1s fully-settled
 * (5 half-lives, ~99% settled). Matches Aesthetic Brief Rule 10's "400ms
 * crossfade" spec for the perceptual window.
 */
export const CROSSFADE_HALF_LIFE_MS = 200;
